import React, { useEffect } from "react";
import { BackButton, Button } from "../../components";
import { PiCheckBold } from "react-icons/pi";
import { useNavigate, useSearchParams } from "react-router-dom";
import PurchaseDetails from "./PurchaseDetails";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CustomerDetails from "./CustomerDetails";
import StoreDetails from "./StoreDetails";
import DamagedItems from "./DamagedItems";
import NikonReviewClaimRequest from "./ReviewClaimRequest";
import { claimStepsData } from "./types";
import RouteNames from "../../routes/type";
import { convertDate, navigateTo } from "../../helper";
import queryString from "query-string";
import SubmitClaim from "./SubmitClaim";
import { connect, useDispatch } from "react-redux";
import { submitClaim } from "../../store/claim/actions";
import { ClaimScope } from "../../store/claim/types";
import { RootState } from "../../store/root-reducer";
import { isScopeLoading } from "../../store/scope-loading/helpers";

const nikonClaimStepsData = [
  { step_num: 1 },
  { step_num: 2 },
  { step_num: 3 },
  { step_num: 4 },
  { step_num: 5 },
];

function NikonStep({ loading }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const formMethods = useForm({});
  const [searchParams] = useSearchParams();
  const stepId: number = parseInt(searchParams.get("step") || "");

  const currentStep = claimStepsData.find((item) => item.step_num === stepId);
  const { handleSubmit } = formMethods;

  useEffect(() => {
    if ((!stepId && !/^[0-9]*$/.test(`${stepId}`)) || stepId > 6) {
      navigate(`${RouteNames.CREATE_CLAIM}?step=1`, { replace: true });
    }
    return () => {};
  }, [navigate, stepId]);

  // useEffect(() => {
  //   const handleBeforeUnload = (event) => {
  //     event.preventDefault();
  //     event.returnValue = true;
  //   };
  //   window.addEventListener("beforeunload", handleBeforeUnload);
  //   return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  // }, []);

  const navigateToNextStep = (step) => {
    const parsed = queryString.parse(window.location.search);
    window.scrollTo(0, 0);
    navigateTo(
      `${RouteNames.CREATE_CLAIM}?${queryString.stringify({
        ...parsed,
        step: step ?? stepId + 1,
      })}`
    );
  };

  const handleNext = (values) => {
    switch (stepId) {
      case 1:
        navigateToNextStep(2);
        break;
      case 2:
        navigateToNextStep(3);
        break;
      case 3:
        navigateToNextStep(4);
        break;
      case 4:
        navigateToNextStep(5);
        break;
      case 5:
        const mappedValues = structuredClone(values);
        mappedValues.purchase_info.date_of_purchase = convertDate(
          mappedValues.purchase_info.date_of_purchase
        );
        for (const key in mappedValues.damage) {
          if (Object.prototype.hasOwnProperty.call(mappedValues.damage, key)) {
            mappedValues.damage[key].date_of_damage = convertDate(
              mappedValues.damage[key].date_of_damage
            );
          }
        }
        dispatch(submitClaim(mappedValues));
        break;
      default:
        break;
    }
  };

  if (stepId === 6) {
    return <SubmitClaim />;
  }

  return (
    <div className="px-4 space-y-3">
      <div className="mt-4 flex justify-between items-center">
        <BackButton onClick={() => navigate(-1)} />
        <div className="flex flex-row gap-2">
          {nikonClaimStepsData.map((item) => {
            const isCompleted = item.step_num < stepId;
            const isActive = item.step_num === stepId;
            return (
              <div
                key={item.step_num}
                className={`size-7 flex items-center justify-center rounded-full ${
                  isCompleted ? "bg-yellow-400 text-black" : ""
                } ${isActive ? "bg-yellow-400 text-black" : ""} ${
                  !isCompleted && !isActive ? "bg-gray-300 text-gray-500 " : ""
                }`}
              >
                {isCompleted ? <PiCheckBold size={16} /> : item.step_num}
              </div>
            );
          })}
        </div>
      </div>
      <form onSubmit={handleSubmit(handleNext)}>
        <h3 className="text-2xl font-semibold mb-3">{t(currentStep?.title)}</h3>
        <FormProvider {...formMethods}>
          {stepId === 1 && <PurchaseDetails />}
          {stepId === 2 && <CustomerDetails />}
          {stepId === 3 && <StoreDetails />}
          {stepId === 4 && <DamagedItems />}
          {stepId === 5 && <NikonReviewClaimRequest />}
        </FormProvider>
        <Button
          submit
          isLoading={loading}
          className="my-4 bg-brand-color !text-black hover:bg-brand-hover rounded-xl"
        >
          {stepId === 5 ? t("Submit") : t("Next")}
        </Button>
      </form>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  loading: isScopeLoading(state.scopeLoading, ClaimScope),
});

export default connect(mapStateToProps)(NikonStep);
